/**
 * Secrets on the crag tops and roofs of the new Yanyu (docs/systems/FUN_P1.md section 2): seven vistas (จุดชมวิว) and nine
 * chests (หีบสมบัติ). One is found by standing in it (`SECRET.radius`, within `SECRET.band` of its height above the
 * sea): no button. Rules: systems/secrets.ts. Data only: add a secret here.
 */
import { JIUZHOU_SECRETS } from './regions/jiuzhou.content';
import type { ZoneId } from './zones';

export interface SecretDef {
  readonly id: string;
  readonly kind: 'vista' | 'chest';
  readonly name: string;
  readonly zone: ZoneId;
  readonly x: number;
  readonly y: number;
  /** Height above the sea (m). */
  readonly h: number;
  /** A chest's tier (1 roof or boat, 2 crag, 3 hidden). */
  readonly tier?: 1 | 2 | 3;
}

export const SECRET = {
  radius: 2.5,
  band: 1.5,
  /** A vista's first visit pays this many แต้มฝึกปรือ. */
  vistaCultivation: 30,
  /** A chest refills once per game day with this share of its ตำลึง (and no item, no แต้มฝึกปรือ). */
  refillShare: 0.25,
  /** Vistas seen before the hidden master shows himself. */
  vistasForMaster: 4,
} as const;

export const CHEST_REWARDS: Readonly<Record<1 | 2 | 3, { readonly gold: number; readonly cultivation: number }>> = {
  1: { gold: 40, cultivation: 30 },
  2: { gold: 120, cultivation: 80 },
  3: { gold: 200, cultivation: 150 },
};

const v = (id: string, name: string, x: number, y: number, h: number): SecretDef => ({ id, kind: 'vista', name, zone: 'yanyu2', x, y, h });
const c = (id: string, tier: 1 | 2 | 3, name: string, x: number, y: number, h: number): SecretDef => ({ id, kind: 'chest', tier, name, zone: 'yanyu2', x, y, h });

export const SECRETS: readonly SecretDef[] = [
  v('v.shrine', 'หลังคาศาลเจ้ากลางน้ำ', 402, 370, 9.6),
  v('v.hall', 'สันหลังคาหอหกสำนัก', 300, 211, 12),
  v('v.falls', 'ยอดน้ำตกต้นน้ำ', 384, 3, 24),
  v('v.mist-peak', 'ขอบผายอดหมอก', 74, 118, 90),
  v('v.dim-moon', 'ยอดผาจันทร์มัว', 562, 92, 100),
  v('v.cinnabar', 'ปลายผาดาบชาด', 596, 194, 80),
  v('v.mast', 'ยอดเสากระโดงเรือสำเภา', 293, 571, 11),
  c('c.boat', 1, 'เรือจอดกลางน้ำ', 391, 324, 2),
  c('c.market', 1, 'หลังคาแผงตลาดฝั่งตะวันตก', 350, 236, 5.8),
  c('c.eastroof', 1, 'หลังคาบ้านท้ายย่านฝั่งตะวันออก', 530, 400, 5.8),
  c('c.shrine', 1, 'หลังศาลเจ้ากลางน้ำ', 406, 379, 9.6),
  c('c.bell-stone', 2, 'ขอบผาหินระฆังด้านตะวันออก', 200, 124, 75),
  c('c.cinnabar', 2, 'ใต้ต้นสนบนผาดาบชาด', 594, 140, 80),
  c('c.gap', 3, 'หิ้งหินในช่องผายอดหมอก–หินระฆัง', 136, 100, 50),
  c('c.falls', 3, 'ถ้ำหลังม่านน้ำตก', 384, 7, 12),
  c('c.pier', 3, 'ปลายท่าเรือฝั่งตะวันออกกลางทะเล', 503, 588, 0.3),
];

/** The region's own secrets (data/regions/jiuzhou.content.ts): in region metres, found only on a region map. */
export const REGION_SECRETS: readonly SecretDef[] = JIUZHOU_SECRETS;

export const secretById = (id: string): SecretDef | undefined => SECRETS.find((s) => s.id === id) ?? REGION_SECRETS.find((s) => s.id === id);
